import React from 'react';
import { IMaskInput } from 'react-imask';
import AddIcon from '@mui/icons-material/Add';

export interface FieldInputAddProps {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  onAdd: () => void;
  error?: string;
  placeholder?: string;
  type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
  required?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  className?: string;

  mask?: string | RegExp | NumberConstructor | DateConstructor | Record<string, unknown>;
  unmask?: boolean | 'typed';
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode'];
}

export const FieldInputAdd: React.FC<FieldInputAddProps> = ({
  label,
  value,
  onChange,
  onAdd,
  error,
  placeholder,
  type = 'text',
  required = false,
  disabled = false,
  icon,
  className,
  mask,
  unmask = false,
  inputMode,
}) => {

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!disabled) {
        onAdd();
      }
    }
  };

  const inputClass = [
    'block w-full rounded-l-md border px-3 py-2 shadow-sm',
    'focus:outline-none focus:z-10 focus:ring-2',
    error
      ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
      : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500',
    icon ? 'pl-10' : '',
    disabled ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-600' : '',
  ]
    .join(' ')
    .trim();

  const buttonClass = [
    '-ml-px relative inline-flex items-center justify-center rounded-r-md border px-4 text-sm font-medium',
    'focus:outline-none focus:ring-1 focus:z-10',
    'transition-colors',
    error
      ? 'border-red-300 bg-red-50 text-red-600 hover:bg-red-100 focus:ring-red-500 focus:border-red-500'
      : 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300 focus:ring-blue-500 focus:border-blue-500',
    disabled ? 'cursor-not-allowed opacity-50' : '',
  ]
    .join(' ')
    .trim();

  const errorId = error && label ? `${label.replace(/\s+/g, '-').toLowerCase()}-error` : `field-error-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* Flex container для инпута и кнопки */}
      <div className="flex shadow-sm rounded-md relative">

        {/* Иконка слева (если есть) */}
        {icon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 z-20">
            <span className="text-gray-500 sm:text-sm">{icon}</span>
          </div>
        )}

        {/* Поле ввода */}
        <div className="relative flex-grow focus-within:z-10">
          {mask ? (
            <IMaskInput
              mask={mask as any}
              value={value}
              unmask={unmask}
              onAccept={(val) => onChange(String(val))}
              placeholder={placeholder}
              disabled={disabled}
              className={inputClass}
              inputMode={inputMode}
              type={type}
              aria-invalid={!!error}
              aria-describedby={error ? errorId : undefined}
              onKeyDown={handleKeyDown}
            />
          ) : (
            <input
              type={type}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              disabled={disabled}
              className={inputClass}
              inputMode={inputMode}
              aria-invalid={!!error}
              aria-describedby={error ? errorId : undefined}
              onKeyDown={handleKeyDown}
            />
          )}
        </div>

        {/* Кнопка Плюс */}
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          className={buttonClass}
          aria-label="Добавить"
        >
          <AddIcon fontSize="small" />
        </button>
      </div>

      {/* Сообщение об ошибке с анимацией */}
      {error && (
        <div
          id={errorId}
          className="mt-1 text-xs text-red-600 flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200"
          role="alert"
        >
          <svg
            className="w-3 h-3 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};